// Wiring a tapped push notification into the SAME Pending Action pipeline a
// Universal Link/App Link tap uses (#1695, spec.md §7.3: "כל סוגי ההתראות
// משתמשים ב-deep link contract אחד ל-Web ול-Native"). Deliberately NOT a
// second contract: a push's `route` is synthesized into the app's own
// kravcalc:// custom-scheme URL and handed to the exact same
// normalizeDeepLink() this file's sibling, nativeLinkListener.js, uses —
// allowlist validation, origin checks and the resolved/pending split all
// come from there, unchanged.
//
// Covers the same three states nativeLinkListener.js does: a tap while the
// app is open (`pushTapped` event), a tap that resumes a backgrounded app
// (also `pushTapped` — the OS, not this code, decides whether that's a
// resume or a cold start), and a cold start from a tap while the app was
// killed (`getLastTappedPush`, this plugin's equivalent of App's
// getLaunchUrl — see NativePushPlugin.swift/.java).

import { NativeNotifications } from '../platform/adapters/nativeNotifications'
import { normalizeDeepLink, DEEP_LINK_CUSTOM_SCHEME } from './normalizeDeepLink'
import { storePendingAction } from './pendingActionStorage'
import { isNativePlatform } from '../platform/runtime'

/**
 * @param {?string} route e.g. "/employers/2/anomalies" (data.route on the
 *   push payload — functions/notifications/deliverToChannels.js already
 *   builds this with no PII, and #1695 forwards it unchanged).
 * @return {?string} a kravcalc://... URL normalizeDeepLink() can parse, or
 *   null when there is no usable route.
 */
function routeToDeepLinkUrl(route) {
  const path = typeof route === 'string' ? route.trim() : ''
  if (!path.startsWith('/') || path.length < 2) return null
  return `${DEEP_LINK_CUSTOM_SCHEME}${path.slice(1)}`
}

async function handleTappedPush(data, { hasSession, onResolvedAction }) {
  const url = routeToDeepLinkUrl(data?.route)
  if (!url) return

  const action = normalizeDeepLink(url)
  if (!action) return

  if (hasSession()) {
    onResolvedAction(action)
    return
  }

  await storePendingAction(action)
}

/**
 * Registers the `pushTapped` listener and handles a cold-start tap.
 * Same `hasSession()`/`onResolvedAction(action)` contract as
 * `registerNativeLinkListener` — see that file's own comment.
 *
 * @param {object} params Parameters.
 * @param {function(): boolean} params.hasSession
 * @param {function(object): void} params.onResolvedAction
 * @return {function(): void} cleanup. A no-op on web.
 */
export function registerNativePushTapListener({ hasSession, onResolvedAction }) {
  if (!isNativePlatform()) return () => {}

  let removed = false
  let handle = null

  NativeNotifications.addListener('pushTapped', ({ data }) => {
    handleTappedPush(data, { hasSession, onResolvedAction })
  }).then(h => {
    if (removed) {
      h.remove()
    } else {
      handle = h
    }
  }).catch(() => {})

  if (typeof NativeNotifications.getLastTappedPush === 'function') {
    NativeNotifications.getLastTappedPush()
      .then(result => {
        if (result?.data) {
          handleTappedPush(result.data, { hasSession, onResolvedAction })
        }
      })
      .catch(() => {})
  }

  return () => {
    removed = true
    if (handle) {
      handle.remove()
      handle = null
    }
  }
}
